import { FC, FormEvent, useEffect, useState } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { ActionRow } from '@savgroup-front-common/core/src/atoms/ActionRow';
import { Button } from '@savgroup-front-common/core/src/atoms/button';
import { Heading } from '@savgroup-front-common/core/src/atoms/Heading';

import {
  getPublishModalDescription,
  getPublishModalTitle,
} from './submitRulePage.helpers';
import type { PublishScope } from './submitRulePage.types';
import {
  $ModalDescription,
  $ModalError,
  $ModalFieldLabel,
  $ModalTextarea,
} from './SubmitRulePage.styles';

interface SubmitRuleModalProps {
  scope: PublishScope;
  onSubmit: (comment: string) => void;
  isLoading?: boolean;
}

const SubmitRuleModal: FC<SubmitRuleModalProps> = ({
  scope,
  onSubmit,
  isLoading = false,
}) => {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setComment('');
    setError(undefined);
  }, [scope]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = comment.trim();

    if (!trimmed) {
      setError('Un commentaire est requis pour publier les règles.');

      return;
    }

    setError(undefined);
    onSubmit(trimmed);
  };

  const submitLabel =
    scope.type === 'block' ? 'Publier' : `Publier tout (${scope.ruleCount})`;

  return (
    <form onSubmit={handleSubmit}>
      <Heading level={1}>{getPublishModalTitle(scope)}</Heading>
      <$ModalDescription>{getPublishModalDescription(scope)}</$ModalDescription>
      <div style={{ marginTop: '1.25rem' }}>
        <$ModalFieldLabel htmlFor="submit-rule-comment">
          Commentaire de publication
          <span style={{ color: '#d32f2f' }}> *</span>
        </$ModalFieldLabel>
        <$ModalTextarea
          id="submit-rule-comment"
          value={comment}
          onChange={(event) => {
            setComment(event.target.value);
            if (error) {
              setError(undefined);
            }
          }}
          placeholder="Décrivez les changements apportés aux règles…"
          data-testid="submit-rule-comment"
        />
        {error && <$ModalError>{error}</$ModalError>}
      </div>
      <ActionRow>
        <Button type={BUTTON_TYPES.SUBMIT} primary isLoading={isLoading}>
          {submitLabel}
        </Button>
      </ActionRow>
    </form>
  );
};

SubmitRuleModal.displayName = 'SubmitRuleModal';

export default SubmitRuleModal;
